import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import ChecksTable from 'dashboard/components/ChecksTable';
import { VerificationDetailProps } from 'dashboard/components/VerificationDetail/types';
import {
  AssociatedPerson,
  PersonName,
  VerificationDatabaseBusiness,
} from 'dashboard/models/verification';
import Spacer from 'design-system/components/Spacer';
import TabBar, { Tab } from 'design-system/components/TabBar';
import { VerificationStatus } from 'lib/verification';

const StyledAttributeGrid = styled(AttributeGrid)`
  padding: 0 ${Spacing[4]} ${Spacing[2]};
`;

interface DatabaseBusinessVerificationProps extends VerificationDetailProps {
  verification: VerificationDatabaseBusiness;
}

export const DatabaseBusinessVerification = ({
  verification,
}: DatabaseBusinessVerificationProps) => {
  const {
    'business-name': businessName,
    'registration-number': registrationNumber,
    'address-street-1': addressStreet1,
    'address-street-2': addressStreet2,
    'address-city': addressCity,
    'address-subdivision': addressSubdivision,
    'address-postal-code': addressPostalCode,
    'address-country-code': addressCountryCode,
    'associated-persons': associatedPersons,
    'redacted-at': redactedAt,
    status,
  } = verification.attributes;

  const isRedacted = redactedAt != null;
  const notInitiated = status !== VerificationStatus.Initiated;
  const checks = verification.attributes.checks;
  if (notInitiated && checks) {
    return (
      <>
        <VerificationInputsWrapper>
          <VerificationInputs
            businessName={businessName}
            registrationNumber={registrationNumber}
            addressStreet1={addressStreet1}
            addressStreet2={addressStreet2}
            addressCity={addressCity}
            addressSubdivision={addressSubdivision}
            addressPostalCode={addressPostalCode}
            addressCountryCode={addressCountryCode}
            associatedPersons={associatedPersons}
            isRedacted={isRedacted || false}
          />
        </VerificationInputsWrapper>
        <Spacer height={4} />
        <ChecksTable checks={checks} verification={verification} />
      </>
    );
  } else {
    return (
      <StyledAttributeGrid>
        <AttributeItem title='Business name' value={businessName} isRedacted={isRedacted} />
        <AttributeItem
          title='Registration Number'
          value={registrationNumber}
          isRedacted={isRedacted}
          toggleable
        />
        <AttributeItem
          title='Address'
          value={[
            addressStreet1,
            addressStreet2,
            [[addressCity, addressSubdivision].filter(Boolean).join(' '), addressPostalCode]
              .filter(Boolean)
              .join(', '),
            addressCountryCode,
          ]}
          isRedacted={isRedacted}
        />
      </StyledAttributeGrid>
    );
  }
};

export const VerificationInputs = ({
  businessName,
  registrationNumber,
  addressStreet1,
  addressStreet2,
  addressCity,
  addressSubdivision,
  addressPostalCode,
  addressCountryCode,
  associatedPersons = [],
  isRedacted,
}: {
  businessName: string | undefined;
  registrationNumber: string | undefined;
  addressStreet1: string | undefined;
  addressStreet2: string | undefined;
  addressCity: string | undefined;
  addressSubdivision: string | undefined;
  addressPostalCode: string | undefined;
  addressCountryCode: string | undefined;
  associatedPersons: AssociatedPerson[] | undefined;
  isRedacted: boolean;
}) => {
  let associatedPersonNames = [] as string[];
  if (associatedPersons?.length > 0) {
    if (associatedPersons[0].names?.[0] instanceof String) {
      associatedPersonNames = associatedPersons.map(
        (associatedPerson) => associatedPerson.names?.[0] as string,
      );
    } else {
      associatedPersonNames = associatedPersons.map((associatedPerson) => {
        const personName = associatedPerson.names?.[0] as PersonName;
        if (personName.name_full) {
          return personName.name_full;
        } else {
          return `${personName.name_first} ${personName.name_last}`;
        }
      });
    }
  }
  return (
    <>
      {/* Note: We plan to add another tab for Raw Record once this is available for both AI and non-AI checks.
      For now, the Raw Inputs tab is always selected. */}
      <TabBar>
        <Tab key='tab-raw-inputs' selected={true}>
          Raw Inputs
        </Tab>
      </TabBar>
      <Spacer height={2} />
      <AttributeGrid colCount={4}>
        <AttributeItem
          title='Business Name'
          value={businessName}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__business-name'
        />
        <AttributeItem
          title='Registration Number'
          value={registrationNumber}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__registration-number'
        />
        <AttributeItem
          title='Address Street 1'
          value={addressStreet1}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__address-street-1'
        />
        <AttributeItem
          title='Address Street 2'
          value={addressStreet2}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__address-street-2'
        />
        <AttributeItem
          title='Address City'
          value={addressCity}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__address-city'
        />
        <AttributeItem
          title='Address Subdivision'
          value={addressSubdivision}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__address-subdivision'
        />
        <AttributeItem
          title='Address Postal Code'
          value={addressPostalCode}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__address-postal-code'
        />
        <AttributeItem
          title='Address Country Code'
          value={addressCountryCode}
          placeholder='-'
          isRedacted={isRedacted}
          dataTest='verification-database-business__address-country-code'
        />
        {associatedPersonNames.length > 0 &&
          associatedPersonNames.map((associatedPersonName, i) => (
            <AttributeItem
              key={`verification-database-business__associated-person-${i}`}
              title={`Associated Person #${i + 1}`}
              value={associatedPersonName}
              placeholder='-'
              isRedacted={isRedacted}
              dataTest={`verification-database-business__associated-person-${i}`}
            />
          ))}
      </AttributeGrid>
    </>
  );
};

const VerificationInputsWrapper = styled.div`
  padding: ${Spacing[1]} ${Spacing[4]};
`;
